/**
 * Attorneys-at-Law are enrolled by the Supreme Court; Notaries are commissioned
 * under the Notaries Ordinance. Many attorneys are both (CLAUDE.md §5).
 */
export const PROFESSIONAL_TYPES = ["attorney", "notary", "both"];
/**
 * COMPLIANCE Constraint 5: a professional is not assignable until enrolment is
 * verified and the verification's provenance is stored. Only `verified` is
 * assignable — see `isAssignable`.
 *
 * `revoked` exists because enrolment can be struck off after we verified it;
 * that is a different fact from `rejected` (we checked and it never held up)
 * and the audit trail must be able to tell them apart.
 */
export const VERIFICATION_STATUSES = [
    "unverified",
    "pending",
    "verified",
    "rejected",
    "revoked",
];
/** The single predicate for MD1's gate. Deny-by-default: only `verified` passes. */
export function isAssignable(status) {
    return status === "verified";
}
/**
 * Allowed verification transitions. Written out rather than inferred, because
 * the pairs that are absent are the point:
 *
 *  - nothing reaches `verified` except from `pending`. A professional cannot be
 *    verified straight out of `unverified` — someone must have submitted
 *    something for an admin to have checked.
 *  - `verified` never returns to `pending` or `unverified`. Once an enrolment
 *    has been proven, the only way out is `revoked`, which is a decision with an
 *    author in the audit log, not a quiet reset.
 *  - `rejected` and `revoked` can be re-submitted. An attorney whose enrolment
 *    was struck off can be re-enrolled; a rejection can be a paperwork error.
 */
const VERIFICATION_TRANSITIONS = {
    unverified: ["pending"],
    pending: ["verified", "rejected"],
    verified: ["revoked"],
    rejected: ["pending"],
    revoked: ["pending"],
};
export function canTransitionVerification(from, to) {
    return VERIFICATION_TRANSITIONS[from].includes(to);
}
export function allowedVerificationTransitions(from) {
    return VERIFICATION_TRANSITIONS[from];
}
/**
 * Why an admin refused or withdrew a verification.
 *
 * Codes, not free text, and that is the whole point: the decision is recorded in
 * the append-only audit log, which must never carry personal data, because it
 * can never be erased (COMPLIANCE Constraint 4). A code says what happened
 * without naming anyone. Where a human explanation is genuinely needed, it
 * belongs on the profile, which is erasable — not in the log, which is not.
 */
export const VERIFICATION_REJECTION_REASONS = [
    "ENROLMENT_NOT_FOUND",
    "DETAILS_MISMATCH",
    "EVIDENCE_INSUFFICIENT",
    "DUPLICATE_APPLICATION",
    "OTHER",
];
export const VERIFICATION_REVOCATION_REASONS = [
    "ENROLMENT_STRUCK_OFF",
    "ENROLMENT_SUSPENDED",
    "DETAILS_NO_LONGER_ACCURATE",
    "AT_PROFESSIONALS_REQUEST",
    /**
     * THE PLATFORM'S OWN COMMERCIAL DECISION TO STOP ROUTING WORK TO THIS PERSON.
     *
     * Added for the client-reports flow (./client-report.ts, migration 0021). Every
     * other code in this list is a fact about the professional's ENROLMENT STANDING
     * — struck off, suspended, details stale, or they asked us to stop. None of
     * them fitted, and reaching for the nearest one would have been the whole
     * problem: recording ENROLMENT_STRUCK_OFF because clients complained would be
     * the platform asserting a Supreme Court act that never happened.
     *
     * This code describes OUR decision, not their standing. It asserts NOTHING
     * about the professional's enrolment and NOTHING about their conduct. It says
     * only: this platform has chosen to stop sending it cases. Their enrolment as
     * an attorney-at-law is untouched, is not ours to touch, and a finding of
     * professional misconduct is for the Bar Association of Sri Lanka and the
     * Supreme Court alone.
     *
     * Consequence in this system: verification_status leaves 'verified', so
     * isAssignable() is false and no further case can be routed to them. That is
     * the ONLY removal mechanism — there is deliberately no second one, and no row
     * is deleted (the reports are the evidence for a decision we made).
     *
     * Any UI or export rendering this code must say what it means and what it does
     * not. It must never be shown as, or summarised as, a sanction.
     */
    "WITHDRAWN_BY_PLATFORM",
    "OTHER",
];
/**
 * Which enrolment identifier a profile must carry, given what it claims to be.
 * An attorney is enrolled by the Supreme Court; a notary is commissioned under
 * the Notaries Ordinance; many are both, and both are separately provable.
 */
export function requiredIdentifiers(type) {
    switch (type) {
        case "attorney":
            return ["enrolmentNo"];
        case "notary":
            return ["notaryRegistrationNo"];
        case "both":
            return ["enrolmentNo", "notaryRegistrationNo"];
    }
}
//# sourceMappingURL=professional.js.map